import { useFormikContext } from 'formik';
import { FC } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import styled, { CSSObject } from '@emotion/styled';
import { QcsButton } from '../common/basic/QcsButton';
import { BASE_URL_WITH_VERSION } from '../../utils/api';
import { getMediaToken } from '../../utils/storage';

// Colors and dimensions copied from the email template.
const EmailBackground = styled.div(
  (): CSSObject => ({
    backgroundColor: '#f1f2f3',
    padding: '20px',
  })
);

const EmailContainer = styled.div(
  (): CSSObject => ({
    maxWidth: '660px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    overflow: 'auto',
  })
);

// Mimics <div class="wrapper-client-banner" style="padding: 0 60px;">.
const BannerWrapper = styled.div(
  (): CSSObject => ({
    padding: '20px 60px 0',
  })
);

const Divider = styled.div(
  (): CSSObject => ({
    margin: '10px 60px',
    borderTop: '1px solid #ee7437',
  })
);

const Placeholder = styled.div(
  (): CSSObject => ({
    padding: '20px 60px',
    color: '#999999',
    fontStyle: 'italic',
  })
);

//There is nothing to preview until both a logo and a height are set.
const useHasEmailLogo = () => {
  const { values } = useFormikContext<any>();

  const bannerUrl = _.get(values, 'bannerUrl');
  const emailLogoHeight = Number(_.get(values, 'emailLogoHeight'));

  return !!bannerUrl && !!emailLogoHeight && emailLogoHeight > 0;
};

interface ToggleProps {
  show: boolean;
  onToggle: () => void;
}

//Separate from the preview itself so it can sit in the same action row as the upload button.
export const EmailLogoPreviewToggle: FC<ToggleProps> = ({ show, onToggle }) => {
  const { t } = useTranslation();
  const hasEmailLogo = useHasEmailLogo();

  if (!hasEmailLogo) {
    return null;
  }

  //fullWidth so the button lines up with the height field above it in the section grid.
  return (
    <QcsButton onClick={onToggle} variant="outlined" fullWidth>
      {t(show ? 'admin.company.emailLogoPreviewHide' : 'admin.company.emailLogoPreviewShow')}
    </QcsButton>
  );
};

interface Props {
  show: boolean;
}

export const EmailLogoPreview: FC<Props> = ({ show }) => {
  const { values } = useFormikContext<any>();
  const { t } = useTranslation();
  const hasEmailLogo = useHasEmailLogo();
  const mediaToken = getMediaToken();

  const bannerUrl = _.get(values, 'bannerUrl');
  const emailLogoHeight = Number(_.get(values, 'emailLogoHeight'));

  if (!show || !hasEmailLogo) {
    return null;
  }

  return (
    <EmailBackground>
      <EmailContainer>
        <BannerWrapper>
          <img alt="" style={{ height: `${emailLogoHeight}px` }} src={`${BASE_URL_WITH_VERSION}${bannerUrl}?token=${mediaToken}`} />
        </BannerWrapper>
        <Divider />
        <Placeholder>{t('admin.company.emailLogoPreviewContent')}</Placeholder>
        <Divider />
        <Placeholder>{t('admin.company.emailLogoPreviewSignature')}</Placeholder>
      </EmailContainer>
    </EmailBackground>
  );
};
